import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Users } from 'lucide-react';
import { useApi, type AssignmentLearner, type CourseAssignment } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import { Badge, Button, Card, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';
import { useOrgContext } from './layout';

/** A date input's value (YYYY-MM-DD) as the end of that day in the viewer's zone. */
const endOfDay = (date: string) => new Date(`${date}T23:59:00`).toISOString();
/** An instant as a date input's value, in the viewer's zone. */
const toDateInput = (at: string | null) => {
  if (!at) return '';
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Courses this organization asks its members (and those of its child
 * organizations) to take, with due dates and progress. Members see them on
 * Home and get emails when assigned, three days before the due date and
 * after it passes if they haven't finished.
 */
export function OrgAssignmentsPage() {
  const { org, canManage } = useOrgContext();
  const api = useApi();
  const list = useQuery({
    queryKey: ['assignments', org.id],
    queryFn: () => api.assignments(org.id),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Assigned courses appear at the top of members' Home pages. Members get an email when a
        course is assigned, a reminder three days before it's due, and a note if the date passes.
        Assignments include child organizations, unless you assign to some groups only.
      </p>
      {canManage && <AssignForm orgId={org.id} />}
      {list.isError && <ErrorText error={list.error} />}
      {list.data?.assignments.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">No courses assigned yet.</p>
        </Card>
      )}
      {list.data?.assignments.map((a) => (
        <AssignmentCard key={a.id} orgId={org.id} assignment={a} canManage={canManage} />
      ))}
    </div>
  );
}

function AssignForm({ orgId }: { orgId: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const courses = useQuery({
    queryKey: ['assignableCourses', orgId],
    queryFn: () => api.assignableCourses(orgId),
  });
  const groups = useQuery({ queryKey: ['groups', orgId], queryFn: () => api.groups(orgId) });
  const [courseId, setCourseId] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  // Everyone in the org and its child orgs, or only some of this org's groups.
  const [who, setWho] = useState<'everyone' | 'groups'>('everyone');
  const [picked, setPicked] = useState<string[]>([]);
  const groupList = groups.data?.groups ?? [];
  const assign = useMutation({
    mutationFn: () =>
      api.assignCourse(orgId, {
        courseId,
        dueAt: due ? endOfDay(due) : null,
        note: note.trim() || null,
        groupIds: who === 'groups' ? picked : null,
      }),
    onSuccess: () => {
      setCourseId('');
      setDue('');
      setNote('');
      setWho('everyone');
      setPicked([]);
      void queryClient.invalidateQueries({ queryKey: ['assignments', orgId] });
    },
  });
  const today = toDateInput(new Date().toISOString());

  if (courses.data?.courses.length === 0) {
    return (
      <Card title="Assign a course">
        <p className="text-sm text-muted-foreground">
          Publish a course first (or use one of Nirvik's public courses), then assign it here.
        </p>
      </Card>
    );
  }
  return (
    <Card title="Assign a course">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          assign.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Course" htmlFor="as-course">
            <Select
              id="as-course"
              required
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
            >
              <option value="">Choose a course…</option>
              {courses.data?.courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                  {c.fromLibrary ? ' (Nirvik)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Due date (optional)" htmlFor="as-due">
            <Input
              id="as-due"
              type="date"
              min={today}
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
        </div>
        {groupList.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Who</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="as-who"
                className="size-5 accent-primary"
                checked={who === 'everyone'}
                onChange={() => setWho('everyone')}
              />
              Everyone, including child organizations
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="as-who"
                className="size-5 accent-primary"
                checked={who === 'groups'}
                onChange={() => setWho('groups')}
              />
              Only some groups
            </label>
            {who === 'groups' && (
              <div className="ml-7 grid gap-1 sm:grid-cols-2">
                {groupList.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      className="size-5 accent-primary"
                      checked={picked.includes(g.id)}
                      onChange={(e) =>
                        setPicked((p) =>
                          e.target.checked ? [...p, g.id] : p.filter((id) => id !== g.id),
                        )
                      }
                    />
                    {g.name}
                    <span className="text-muted-foreground">({g.members})</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}
        <Field
          label="Note to learners (optional)"
          htmlFor="as-note"
          hint="Included in the email, e.g. why it matters or when you'll discuss it."
        >
          <Textarea
            id="as-note"
            rows={2}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={!courseId || (who === 'groups' && !picked.length) || assign.isPending}
        >
          {who === 'groups' ? 'Assign to these groups' : 'Assign to all members'}
        </Button>
        <ErrorText error={assign.error} />
      </form>
    </Card>
  );
}

function AssignmentCard({
  orgId,
  assignment: a,
  canManage,
}: {
  orgId: string;
  assignment: CourseAssignment;
  canManage: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [due, setDue] = useState(toDateInput(a.dueAt));
  const [showLearners, setShowLearners] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['assignments', orgId] });
  const update = useMutation({
    mutationFn: () =>
      api.updateAssignment(orgId, a.id, {
        dueAt: due ? endOfDay(due) : null,
        note: a.note,
        groupIds: a.groupIds,
      }),
    onSuccess: () => {
      setEditing(false);
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteAssignment(orgId, a.id),
    onSuccess: refresh,
  });
  const editable = canManage && !a.inherited;
  const pastDue = a.dueAt && new Date(a.dueAt).getTime() < Date.now();

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/c/${a.courseSlug}`} className="font-medium hover:underline">
            {a.courseTitle}
          </Link>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <CalendarClock className="size-4" aria-hidden />
            {a.dueAt ? (
              <span className={pastDue ? 'text-destructive' : ''}>
                {pastDue ? 'Was due' : 'Due'} {formatDate(a.dueAt)}
              </span>
            ) : (
              'No due date'
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {a.groups && (
            <Badge>
              {a.groups.length ? `For ${a.groups.join(', ')}` : 'Its groups were deleted'}
            </Badge>
          )}
          {a.inherited && <Badge>Assigned by {a.assignedBy}</Badge>}
        </div>
      </div>
      {a.note && <p className="mt-3 text-sm">{a.note}</p>}

      <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {(
          [
            ['Members', a.learners],
            ['Completed', a.completed],
            ['In progress', a.inProgress],
            ['Overdue', a.overdue],
          ] as const
        ).map(([label, value]) => (
          <div key={label}>
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="text-2xl font-semibold tabular-nums">{value.toLocaleString()}</dd>
          </div>
        ))}
      </dl>
      <progress
        max={a.learners || 1}
        value={a.completed}
        aria-label={`${a.completed} of ${a.learners} members completed`}
        className="mt-3 h-1.5 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
      />

      {editing ? (
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate();
          }}
        >
          <Field label="Due date" htmlFor={`due-${a.id}`}>
            <Input
              id={`due-${a.id}`}
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={update.isPending}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <ErrorText error={update.error} />
        </form>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {canManage && (
            <Button variant="secondary" onClick={() => setShowLearners((v) => !v)}>
              <Users className="size-4" aria-hidden />
              {showLearners ? 'Hide members' : 'Members’ progress'}
            </Button>
          )}
          {editable && (
            <>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Change due date
              </Button>
              <Button
                variant="ghost"
                disabled={remove.isPending}
                onClick={() =>
                  window.confirm(
                    `Unassign "${a.courseTitle}"? Members keep their progress; it just leaves their Home list of assigned courses.`,
                  ) && remove.mutate()
                }
              >
                Unassign
              </Button>
            </>
          )}
        </div>
      )}
      <ErrorText error={remove.error} />
      {showLearners && <Learners orgId={orgId} assignmentId={a.id} />}
    </Card>
  );
}

const STATUS: Record<AssignmentLearner['status'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

function Learners({ orgId, assignmentId }: { orgId: string; assignmentId: string }) {
  const api = useApi();
  const learners = useQuery({
    queryKey: ['assignmentLearners', orgId, assignmentId],
    queryFn: () => api.assignmentLearners(orgId, assignmentId),
  });
  if (learners.isPending) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>;
  if (learners.isError) return <ErrorText error={learners.error} />;
  return (
    <ul className="mt-4 divide-y divide-border rounded-lg border border-border">
      {learners.data.learners.map((l) => (
        <li key={l.userId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{l.name ?? l.email}</span>
            {l.name && (
              <span className="block truncate text-xs text-muted-foreground">{l.email}</span>
            )}
          </span>
          <span className="text-right text-sm">
            {STATUS[l.status]}
            {l.status === 'in_progress' && ` · ${l.percent}%`}
            <span className="block text-xs text-muted-foreground">
              {l.completedAt
                ? formatDate(l.completedAt)
                : l.lastActiveAt
                  ? `Active ${formatDate(l.lastActiveAt)}`
                  : ''}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
